/**
 * The one hook a widget container uses to get its data.
 *
 *   const { status, data, retry } = useWidgetData('mention-volume-over-time');
 *
 * It reads the active source, the applied (debounced) filters and the page's
 * filters bar type, builds the canonical query, and maps RTK Query state to a
 * card status. `currentData` (not `data`) is used on purpose: while filters
 * change, the card shows its loading state instead of stale numbers — the
 * prototype's behaviour.
 */
import { useMemo } from 'react';

import type { WidgetData, WidgetId, WidgetParams } from '@/data-sources/contract';
import { isApiError, type ApiError } from '@/lib/errors';

import { useAppliedFilters } from '../data/AppliedFiltersContext';
import { useDataSource } from '../data/DataSourceContext';
import { usePageMeta } from '../data/PageContext';
import { canonicalQuery } from '../filters/canonicalQuery';
import { useReportScope, withReportScope } from '../filters/useReportScope';
import { widgetsApi } from './widgets.api';

export type WidgetStatus = 'loading' | 'ready' | 'error' | 'notConnected';

export type WidgetResult<W extends WidgetId> = {
  status: WidgetStatus;
  data: WidgetData<W> | undefined;
  error: ApiError | undefined;
  retry: () => void;
};

type BaseOptions = {
  skip?: boolean;
};

/** Params may be left out where the widget takes none, or takes them optionally. */
type Options<W extends WidgetId> =
  undefined extends WidgetParams<W>
    ? BaseOptions & { params?: WidgetParams<W> }
    : BaseOptions & { params: WidgetParams<W> };

export function useWidgetData<W extends WidgetId>(
  id: W,
  ...[options]: undefined extends WidgetParams<W> ? [Options<W>?] : [Options<W>]
): WidgetResult<W> {
  const { source } = useDataSource();
  const filters = useAppliedFilters();
  const { filtersType } = usePageMeta();
  // On a custom report, the report scopes its widgets (campaign posts).
  const dashboardId = useReportScope();
  const query = useMemo(
    () => withReportScope(canonicalQuery(filters, filtersType), dashboardId),
    [filters, filtersType, dashboardId],
  );

  const result = widgetsApi.useWidgetQuery(
    { source, params: { id, query, params: options?.params } },
    { skip: options?.skip, refetchOnFocus: source === 'live' },
  );

  const error = isApiError(result.error) ? result.error : undefined;
  const status: WidgetStatus = result.isError
    ? error?.kind === 'notConnected'
      ? 'notConnected'
      : 'error'
    : result.currentData === undefined
      ? 'loading'
      : 'ready';

  return {
    status,
    data: result.currentData as WidgetData<W> | undefined,
    error,
    retry: result.refetch,
  };
}
